import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  fixturesCol, squadsWithIds, syncTeamsLayout, syncTeamsWithSquads, teamsLayoutDoc, teamsWithAges,
} from '../utils/teamFixtures';

/**
 * A tour's squads (Team Info), teams (Teams tab) and fixtures, live.
 *
 * With `sync` on, it also keeps the Teams tab in line with the squads — a
 * team per squad, age labels refreshed — writing only when something is out
 * of date. Every signed-in portal that shows teams turns it on, so teams
 * appear as soon as anyone opens the tour after the form is filled in.
 */
export default function useTourTeams(requestId, request, { sync = true } = {}) {
  const { currentUser } = useAuth();
  const [layout, setLayout] = useState(null);
  const [teamInfo, setTeamInfo] = useState(undefined);
  const [fixtures, setFixtures] = useState(null);

  useEffect(() => {
    if (!requestId) return undefined;
    const unsubs = [
      onSnapshot(teamsLayoutDoc(requestId),
        (s) => setLayout(s.exists() ? s.data() : {}),
        () => setLayout({})),
      onSnapshot(doc(db, 'requests', requestId, 'teamInfo', 'data'),
        (s) => setTeamInfo(s.exists() ? s.data() : null),
        () => setTeamInfo(null)),
      onSnapshot(fixturesCol(requestId),
        (s) => setFixtures(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => setFixtures([])),
    ];
    return () => unsubs.forEach((u) => u());
  }, [requestId]);

  const squads = useMemo(() => squadsWithIds(teamInfo?.squads || []), [teamInfo]);
  const loading = layout === null || teamInfo === undefined || fixtures === null;

  // Sync once inputs settle. The transaction re-reads the layout, so a stale
  // snapshot here can only cause a no-op, never a lost edit.
  const lastSync = useRef('');
  useEffect(() => {
    if (!sync || loading || !currentUser || !teamInfo) return;
    const fixtureTeamIds = new Set(fixtures.map((f) => f.teamId));
    const { changed } = syncTeamsWithSquads({
      teams: layout.teams || [], assignments: layout.assignments || {}, squads, request, fixtureTeamIds,
    });
    if (!changed) return;
    const key = JSON.stringify([layout.teams, squads, [...fixtureTeamIds].sort(), request?.departureDate]);
    if (lastSync.current === key) return;
    lastSync.current = key;
    syncTeamsLayout(requestId, { squads, request, fixtureTeamIds, user: currentUser })
      .catch((err) => console.warn('Team sync skipped', err));
  }, [sync, loading, currentUser, teamInfo, layout, squads, fixtures, request, requestId]);

  const teams = useMemo(
    () => teamsWithAges(layout?.teams || [], squads, request),
    [layout, squads, request]
  );
  const teamsById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);
  const assignments = useMemo(() => layout?.assignments || {}, [layout]);

  return {
    loading,
    teams,
    teamsById,
    assignments,
    coachToken: layout?.coachToken || null,
    teamInfo: teamInfo || null,
    squads,
    fixtures: fixtures || [],
  };
}
